import { TooltipIconButton } from "@components/Atoms/TooltipIconButton";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import t from "@ext/localization/locale/translate";
import type { Property } from "@ext/properties/models";
import { FadeReveal } from "@ui-kit/Animate";
import { Icon, type IconCode } from "@ui-kit/Icon";
import { MenuItem } from "@ui-kit/MenuItem";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import type { CSSProperties } from "react";
import { PropertyValue } from "./PropertyValue/PropertyValue";

interface ArticlePropertyRowProps {
	disabled: boolean;
	isAssigned: boolean;
	property: Property;
	onDelete: (propertyId: string) => void;
	onEdit: (propertyId: string) => void;
	onSubmit: (propertyId: string, value?: string) => void;
}

export const ArticlePropertyRow = ({
	disabled,
	isAssigned,
	onDelete,
	onEdit,
	onSubmit,
	property,
}: ArticlePropertyRowProps) => {
	const { attributes, isDragging, listeners, setActivatorNodeRef, setNodeRef, transform, transition } = useSortable({
		disabled,
		id: property.id,
	});
	const style: CSSProperties = {
		opacity: isDragging ? 0.72 : 1,
		position: "relative",
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 1 : 0,
	};

	return (
		<div
			className="grid min-w-0 grid-cols-[minmax(0,8rem)_minmax(0,1fr)] items-start gap-2"
			data-testid="article-property-row"
			ref={setNodeRef}
			style={style}
		>
			<div className="group relative min-w-0">
				<MenuItem
					aria-disabled={disabled}
					aria-label={property.name}
					className="min-h-7 w-full min-w-0 items-center gap-1.5 rounded-lg bg-transparent px-1.5 py-0.5 text-muted-foreground hover:bg-secondary-bg-hover focus:bg-secondary-bg-hover focus-within:bg-secondary-bg-hover"
					data-testid="article-property-name"
					role="button"
					tabIndex={disabled ? -1 : 0}
				>
					<span className="relative size-[14px] shrink-0">
						<Icon
							className="absolute inset-0 size-[14px] transition-opacity duration-100 group-hover:opacity-0 group-focus-within:opacity-0"
							icon={(property.icon as IconCode) || "tag"}
						/>
						{!disabled && (
							<FadeReveal
								className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2"
								isVisible={isDragging}
								revealOnGroupInteraction
							>
								<button
									{...attributes}
									{...listeners}
									aria-label={t("properties.move")}
									className="flex size-6 cursor-grab items-center justify-center text-muted-foreground active:cursor-grabbing"
									onClick={(event) => event.stopPropagation()}
									ref={setActivatorNodeRef}
									type="button"
								>
									<Icon className="size-[14px]" icon="grip-vertical" />
								</button>
							</FadeReveal>
						)}
					</span>
					<TextOverflowTooltip className="min-w-0 text-left text-xs font-normal">
						{property.name}
					</TextOverflowTooltip>
					{!disabled && (
						<FadeReveal
							className="absolute inset-y-0 right-0 z-10 flex items-center rounded-r-lg bg-gradient-to-l from-secondary-bg-hover from-60% to-transparent pl-6 pr-1.5"
							revealOnGroupInteraction
						>
							<TooltipIconButton
								aria-label={t("properties.edit")}
								className="size-5 shrink-0"
								icon="pen"
								iconClassName="shrink-0"
								onClick={() => onEdit(property.id)}
								size="sm"
								tooltip={t("edit2")}
								variant="text"
							/>
						</FadeReveal>
					)}
				</MenuItem>
			</div>
			<div className="min-w-0" data-testid="article-property-value">
				<PropertyValue
					disabled={disabled}
					isAssigned={isAssigned}
					onDelete={onDelete}
					onSubmit={onSubmit}
					property={property}
				/>
			</div>
		</div>
	);
};
